import { Container, Graphics } from 'pixi.js';
import { COLORS, FONT, SCREEN, SHAPE } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { currencyChip, iconButton, label, roundedPanel, spriteFit, textLink, titleChip, toast, type CurrencyChip } from '../ui/components';
import { buyWithFeedback, openStarterOffer, priceButton } from '../ui/offers';
import type { Nav } from '../meta/nav';
import { AD_REWARDS, adCoinsLeft, grantAdCoins, product, today, type ProductId } from '../monetization/products';
import { sound } from '../audio/sound';
import { haptic } from '../audio/haptics';

/**
 * Mockup 32: coins and booster packs with live store prices, exactly one
 * "Best Value" (only when it is really the best rate), free coins for a
 * video, starter pack, "Restore purchases". Everything optional.
 */
export class ShopScreen implements Screen {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly header = new Container();
  private readonly content = new Container();
  private readonly overlay = new Container();
  private coins!: CurrencyChip;
  private l?: Layout;

  constructor(private readonly nav: Nav) {
    const back = iconButton('back', () => nav.showCafe());
    back.position.set(100, 0);
    const title = titleChip('Shop', 240);
    title.position.set(SCREEN.width / 2, 0);
    this.coins = currencyChip('ui_coin', nav.save().coins);
    this.coins.view.position.set(SCREEN.width - 150, 0);
    this.header.addChild(back, title, this.coins.view);
    this.build();
    this.view.addChild(this.bg, this.content, this.header, this.overlay);
  }

  private build(): void {
    const c = this.content;
    c.removeChildren().forEach((x) => x.destroy({ children: true }));
    const best = this.nav.commerce.bestValue();

    const coinsHead = label('Coins', FONT.size.screenTitle, { weight: FONT.weight.heading, anchor: [0, 0.5] });
    coinsHead.position.set(64, 0);
    c.addChild(coinsHead);
    (['coins_small', 'coins_medium', 'coins_large'] as ProductId[]).forEach((id, i) => {
      const card = this.coinCard(id, i + 1, id === best);
      card.position.set(64 + i * 308, 50);
      c.addChild(card);
    });

    const boostHead = label('Boosters', FONT.size.screenTitle, { weight: FONT.weight.heading, anchor: [0, 0.5] });
    boostHead.position.set(64, 470);
    c.addChild(boostHead);
    (['helpers', 'toolkit'] as ProductId[]).forEach((id, i) => {
      const card = this.boosterCard(id);
      card.position.set(64 + i * 462, 520);
      c.addChild(card);
    });

    // free coins for a rewarded video (optional, limited per day)
    const left = adCoinsLeft(this.nav.save(), today());
    const free = new Container();
    free.addChild(roundedPanel(896, 150, { radius: 28 }));
    const coin = spriteFit('ui_coin', 80);
    coin.anchor.set(0.5);
    coin.position.set(80, 75);
    const ft = label(`Watch a video: +${AD_REWARDS.shopCoins} coins`, FONT.size.body, { weight: FONT.weight.heading, anchor: [0, 0.5] });
    ft.position.set(140, 55);
    const fl = label(left > 0 ? `${left} left today` : 'Come back tomorrow!', FONT.size.secondary, { weight: FONT.weight.body, anchor: [0, 0.5] });
    fl.position.set(140, 102);
    free.addChild(coin, ft, fl);
    if (left > 0) {
      const watch = priceButton('▶  Watch', 220, () => void this.watchForCoins(), 76);
      watch.position.set(770, 75);
      free.addChild(watch);
    }
    free.position.set(64, 1000);
    c.addChild(free);

    // starter pack (until bought)
    let y = 1190;
    if (!this.nav.save().purchases.starter) {
      const st = new Container();
      st.addChild(roundedPanel(896, 130, { radius: 28, fill: 'blush' }));
      const band = spriteFit('cosmetic_bandana_paw', 90);
      band.anchor.set(0.5);
      band.position.set(80, 65);
      const t = label('Starter Pack · one-time', FONT.size.body, { weight: FONT.weight.heading, anchor: [0, 0.5] });
      t.position.set(140, 65);
      const look = priceButton('View', 180, () => openStarterOffer(this.overlay, this.l, this.nav, () => this.refresh()), 72);
      look.position.set(790, 65);
      st.addChild(band, t, look);
      st.position.set(64, y);
      c.addChild(st);
      y += 170;
    }

    const once = label('One-time purchases · no subscriptions', FONT.size.secondary, { weight: FONT.weight.body });
    once.position.set(SCREEN.width / 2, y + 10);
    const restore = textLink('Restore purchases', () => void this.restore());
    restore.position.set(SCREEN.width / 2, y + 70);
    c.addChild(once, restore);
    if (this.nav.commerce.testMode) {
      const tm = label('TEST MODE – no real payments', FONT.size.secondary, { weight: FONT.weight.heading, color: 'wood' });
      tm.position.set(SCREEN.width / 2, y + 130);
      c.addChild(tm);
    }
  }

  private coinCard(id: ProductId, stack: number, best: boolean): Container {
    const p = product(id);
    const card = new Container();
    card.addChild(roundedPanel(280, 380, { radius: 28 }));
    for (let i = 0; i < stack; i++) {
      const coin = spriteFit('ui_coin', 84);
      coin.anchor.set(0.5);
      coin.position.set(140 + (i - (stack - 1) / 2) * 46, 110 - (i % 2) * 18);
      card.addChild(coin);
    }
    const amount = label(p.coins.toLocaleString('en-US'), FONT.size.celebration, { weight: FONT.weight.heading });
    amount.position.set(140, 210);
    const unit = label('Coins', FONT.size.secondary, { weight: FONT.weight.body });
    unit.position.set(140, 262);
    card.addChild(amount, unit, this.buyButton(id, 230, 322));
    if (best) {
      const tag = new Container();
      const t = label('Best Value', 24, { weight: FONT.weight.heading });
      tag.addChild(new Graphics().roundRect(-t.width / 2 - 18, -20, t.width + 36, 40, 20).fill(COLORS.butter).stroke({ width: SHAPE.outline, color: COLORS.cocoa }), t);
      tag.position.set(140, 0);
      card.addChild(tag);
    }
    return card;
  }

  private boosterCard(id: ProductId): Container {
    const p = product(id);
    const card = new Container();
    card.addChild(roundedPanel(434, 440, { radius: 28 }));
    const t = label(p.title, FONT.size.cardTitle + 2, { weight: FONT.weight.heading });
    t.position.set(217, 50);
    card.addChild(t);
    const n = p.boosters.hammer ?? 1;
    (['booster_hammer', 'booster_spoon', 'booster_moves5'] as const).forEach((f, i) => {
      const b = spriteFit(f, 96);
      b.anchor.set(0.5);
      b.position.set(217 + (i - 1) * 124, 150);
      const x = label(`×${n}`, FONT.size.body, { weight: FONT.weight.heading });
      x.position.set(217 + (i - 1) * 124, 232);
      card.addChild(b, x);
    });
    const each = label(`${n} of each booster`, FONT.size.secondary, { weight: FONT.weight.body });
    each.position.set(217, 290);
    card.addChild(each, this.buyButton(id, 330, 370));
    return card;
  }

  private buyButton(id: ProductId, w: number, y: number): Container {
    const price = this.nav.commerce.price(id)?.priceString;
    const b = priceButton(price ?? '—', w, () => {
      if (!price || this.nav.commerce.isBusy) return;
      void buyWithFeedback(this.nav, id, this.overlay, (this.l?.safeTop ?? 92) + 200).then((ok) => ok && this.refresh());
    });
    b.position.set(w === 230 ? 140 : 217, y);
    return b;
  }

  private async watchForCoins(): Promise<void> {
    if (this.nav.commerce.isBusy) return;
    const outcome = await this.nav.commerce.watchAd();
    if (outcome === 'rewarded') {
      this.nav.update(grantAdCoins(this.nav.save(), today()));
      sound.play('goal');
      haptic('success');
      toast(this.overlay, `+${AD_REWARDS.shopCoins} coins – thank you!`, (this.l?.safeTop ?? 92) + 200, 'ui_coin');
      this.refresh();
    } else if (outcome === 'unavailable') {
      toast(this.overlay, 'No video available right now.', (this.l?.safeTop ?? 92) + 200);
    }
  }

  private async restore(): Promise<void> {
    const restored = await this.nav.commerce.restore();
    const y = (this.l?.safeTop ?? 92) + 200;
    if (restored === null) toast(this.overlay, 'Could not reach the store. Please try again.', y);
    else toast(this.overlay, restored.length ? 'Your Starter Pack is back!' : 'Everything is already restored.', y);
    this.refresh();
  }

  private refresh(): void {
    this.coins.set(this.nav.save().coins);
    this.build();
    if (this.l) this.layout(this.l);
  }

  layout(l: Layout): void {
    this.l = l;
    this.bg.clear().rect(l.left, 0, l.right - l.left, l.bottom).fill(COLORS.mint);
    this.header.y = l.safeTop + 30;
    // content is ~1500 px tall; shrink it a little on short screens
    const top = l.safeTop + 140;
    const avail = l.safeBottom - top;
    const needed = this.content.height + 20;
    const s = Math.min(1, avail / needed);
    this.content.scale.set(s);
    this.content.position.set((SCREEN.width - SCREEN.width * s) / 2, top + 30);
  }
}
